import { StandardEvents } from '@shopify/events';

const config = JSON.parse(document.querySelector('[data-um-cart-config]').textContent);
const labels = config.labels;
const root = config.root.replace(/\/?$/, '/');
const instances = new Set();
const pendingKey = 'urban-motion-cart-payment-v1';
let sequence = 0;
let paymentBusy = false;
let recovering = false;
let recoveryError = '';
let updateGeneration = 0;
const money = cents => new Intl.NumberFormat(config.locale, { style: 'currency', currency: config.currency }).format(cents / 100);
const fill = (text, values) => Object.entries(values).reduce((result, entry) => result.replaceAll('[' + entry[0] + ']', String(entry[1])), text || '');
const planText = (text, term) => fill(text, { term, remaining: term - 1 });
const propertiesOf = value => Array.isArray(value) ? Object.fromEntries(value) : value && typeof value === 'object' ? value : {};
const sorted = value => JSON.stringify(Object.entries(value || {}).sort((a, b) => a[0].localeCompare(b[0])));
const identity = item => String(item.id) + ':' + (item.selling_plan_allocation?.selling_plan?.id || item.sellingPlan || item.selling_plan || '') + ':' + sorted(propertiesOf(item.properties));
const fingerprint = cart => JSON.stringify(cart.items.map(item => [identity(item), item.quantity, item.final_price ?? item.price]).sort((a, b) => a[0].localeCompare(b[0])));

function validQuantity(quantity, variant) {
  const rule = variant?.quantityRule || {};
  const min = Number(rule.min) || 1;
  const step = Number(rule.increment) || 1;
  return Number.isSafeInteger(quantity) && quantity >= min && (quantity - min) % step === 0
    && (rule.max == null || quantity <= Number(rule.max))
    && (variant?.stockMax == null || quantity <= Number(variant.stockMax));
}

function validPlan(source, deposit, term) {
  return Number.isSafeInteger(source?.price) && source.price > 5000 && Number.isSafeInteger(deposit?.price) && deposit.price > 0
    && Math.abs(source.price - deposit.price * term) <= Math.floor(term / 2);
}

function hydrate(state) {
  const lines = state.items.map(raw => {
    const item = { ...raw, properties: propertiesOf(raw.properties) };
    const current = item.variants.find(v => String(v.id) === String(item.id));
    const key = v => JSON.stringify(v.options.filter((_, index) => index !== item.paymentIndex));
    const matches = current ? item.variants.filter(v => key(v) === key(current)) : [];
    const original = item.paymentIndex < 0 ? current : matches.find(v => v.options[item.paymentIndex] === 'Intero');
    const plans = Object.fromEntries([3, 4].map(term => [term, matches.find(v => v.options[item.paymentIndex] === term + ' rate')]));
    const isRate = current && ['3 rate', '4 rate'].includes(current.options[item.paymentIndex]);
    return { item, current, original, plans, isRate };
  });
  const quantities = new Map();
  for (const line of lines) quantities.set(line.original?.id, (quantities.get(line.original?.id) || 0) + line.item.quantity);
  const total = lines.reduce((sum, line) => sum + (line.original?.price ?? line.item.final_price) * line.item.quantity, 0);
  const minimum = lines.some(line => !line.original || line.original.price <= 5000);
  const sourceValid = lines.length > 0 && lines.every(line => line.original?.available && validQuantity(line.item.quantity, line.original)
    && (line.original.stockMax == null || quantities.get(line.original.id) <= line.original.stockMax));
  const incompatible = state.currency !== 'EUR' || state.currency !== config.currency || state.discount > 0
    || state.count !== state.items.reduce((sum, item) => sum + item.quantity, 0) || lines.some(line =>
    !line.current || line.item.sellingPlan || line.item.requiresSellingPlan || line.item.final_price !== line.current.price
    || (line.item.properties?._um_original_variant && String(line.item.properties._um_original_variant) !== String(line.original?.id)));
  const terms = !minimum && !incompatible && sourceValid ? [3, 4].filter(term => lines.every(line => {
    const deposit = line.plans[term];
    return deposit?.available && validPlan(line.original, deposit, term) && validQuantity(line.item.quantity, deposit);
  })) : [];
  const signature = JSON.stringify([fingerprint(state), total, lines.map(line => [line.original?.id, line.original?.price,
    ...[3, 4].map(term => [line.plans[term]?.id, line.plans[term]?.price, line.plans[term]?.available])])]);
  return { state, lines, total, terms, sourceValid, containsRates: lines.some(line => line.isRate), signature,
    reason: minimum ? labels.minimum : labels.incompatible };
}

function summarize(plan, term) {
  const first = plan.lines.reduce((sum, line) => sum + line.plans[term].price * line.item.quantity, 0);
  return { total: plan.total, first, remaining: plan.total - first, last: plan.total - first * (term - 1) };
}

function originalProperties(item) {
  let properties = propertiesOf(item.properties);
  if (item.properties?._um_original_properties) {
    const value = JSON.parse(item.properties._um_original_properties);
    if (!value || Array.isArray(value) || typeof value !== 'object') throw new Error(labels.changed);
    properties = value;
  }
  const result = { ...properties };
  delete result._um_cart_transaction;
  return result;
}

function paymentItems(plan, term) {
  return plan.lines.map(line => {
    const original = line.original;
    const deposit = line.plans[term];
    const properties = originalProperties(line.item);
    return { id: deposit.id, quantity: line.item.quantity, properties: {
      ...properties,
      [labels.orderTotal]: money(original.price) + ' ' + labels.unit,
      [labels.orderFirst]: money(deposit.price) + ' ' + labels.unit,
      [labels.orderRemaining]: money(original.price - deposit.price) + ' ' + labels.unit,
      [labels.orderPlan]: planText(labels.planName, term),
      [labels.orderConsent]: planText(labels.consent, term),
      [labels.orderOptions]: original.options.filter((_, i) => i !== line.item.paymentIndex).filter(v => v !== 'Default Title').join(' / '),
      _um_payment: 'cart-v1',
      _um_original_variant: String(original.id),
      _um_term: String(term),
      _um_total_cents_unit: String(original.price),
      _um_first_cents_unit: String(deposit.price),
      _um_last_cents_unit: String(original.price - deposit.price * (term - 1)),
      _um_original_properties: JSON.stringify(properties)
    } };
  });
}

function cartUrl(path, section) {
  const address = new URL(root + path, location.origin);
  if (address.origin !== location.origin) throw new Error(labels.unavailable);
  if (section) address.searchParams.set('section_id', section);
  const preview = new URL(location.href).searchParams.get('preview_theme_id');
  if (preview) address.searchParams.set('preview_theme_id', preview);
  return address;
}

async function requestCart(path, body) {
  const response = await fetch(cartUrl(path), { credentials: 'same-origin', cache: 'no-store',
    headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) },
    ...(body ? { method: 'POST', body: JSON.stringify(body) } : {}) });
  const result = await response.json();
  if (!response.ok || result.status >= 400) throw new Error(result.description || labels.unavailable);
  return result;
}

async function fetchState() {
  const response = await fetch(cartUrl('cart', 'um-cart-installment-state'), { credentials: 'same-origin', cache: 'no-store' });
  if (!response.ok) throw new Error(labels.unavailable);
  const part = new DOMParser().parseFromString(await response.text(), 'text/html').querySelector('[data-um-cart-state]');
  if (!part) throw new Error(labels.unavailable);
  return JSON.parse(part.textContent);
}

function pendingRead() {
  try { return JSON.parse(sessionStorage.getItem(pendingKey) || 'null'); } catch { return null; }
}
function pendingWrite(value) {
  if (value) {
    sessionStorage.setItem(pendingKey, JSON.stringify(value));
    if (!sessionStorage.getItem(pendingKey)) throw new Error(labels.unavailable);
  } else sessionStorage.removeItem(pendingKey);
}
const operations = { read: () => requestCart('cart.js'), post: requestCart, save: pendingWrite };
const snapshotItems = cart => cart.items.map(item => ({ id: item.id, quantity: item.quantity, properties: propertiesOf(item.properties),
  ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}) }));

async function recoverCart(ops, transaction) {
  let current = await ops.read();
  const existing = new Map();
  current.items.filter(item => item.properties?._um_cart_transaction !== transaction.id).forEach(item =>
    existing.set(identity(item), (existing.get(identity(item)) || 0) + item.quantity));
  const missing = [];
  for (const item of transaction.originals) {
    const key = identity(item);
    const available = existing.get(key) || 0;
    if (available < item.quantity) missing.push({ ...item, quantity: item.quantity - available });
    existing.set(key, Math.max(0, available - item.quantity));
  }
  if (missing.length) { await ops.post('cart/add.js', { items: missing }); current = await ops.read(); }
  const actual = new Map();
  current.items.filter(item => item.properties?._um_cart_transaction !== transaction.id).forEach(item =>
    actual.set(identity(item), (actual.get(identity(item)) || 0) + item.quantity));
  const expected = new Map();
  transaction.originals.forEach(item => expected.set(identity(item), (expected.get(identity(item)) || 0) + item.quantity));
  if ([...expected].some(entry => (actual.get(entry[0]) || 0) < entry[1])) throw new Error(labels.recovery);
  const updates = Object.fromEntries(current.items.filter(item => item.properties?._um_cart_transaction === transaction.id).map(item => [item.key, 0]));
  if (Object.keys(updates).length) await ops.post('cart/update.js', { updates });
  current = await ops.read();
  if (current.items.some(item => item.properties?._um_cart_transaction === transaction.id)) throw new Error(labels.recovery);
  ops.save(null);
}

async function convertCart(ops, items, expectedFirst, expectedFingerprint) {
  const initial = await ops.read();
  if (fingerprint(initial) !== expectedFingerprint) throw new Error(labels.changed);
  const transaction = { id: 'um-' + Date.now() + '-' + crypto.getRandomValues(new Uint32Array(2)).join('-'), originals: snapshotItems(initial) };
  ops.save(transaction);
  try {
    await ops.post('cart/add.js', { items: items.map(item => ({ ...item, properties: { ...item.properties, _um_cart_transaction: transaction.id } })) });
    let current = await ops.read();
    const additions = current.items.filter(item => item.properties?._um_cart_transaction === transaction.id);
    const originals = current.items.filter(item => item.properties?._um_cart_transaction !== transaction.id);
    if (fingerprint({ items: originals }) !== expectedFingerprint
      || additions.reduce((sum, item) => sum + item.quantity, 0) !== items.reduce((sum, item) => sum + item.quantity, 0)
      || (expectedFirst !== null && additions.reduce((sum, item) => sum + item.final_line_price, 0) !== expectedFirst)) throw new Error(labels.changed);
    await ops.post('cart/update.js', { updates: Object.fromEntries(originals.map(item => [item.key, 0])) });
    current = await ops.read();
    if (current.items.some(item => item.properties?._um_cart_transaction !== transaction.id)
      || (expectedFirst !== null && current.total_price !== expectedFirst)) throw new Error(labels.changed);
    const expected = new Map();
    items.forEach(item => { const key = identity(item); expected.set(key, (expected.get(key) || 0) + item.quantity); });
    const received = new Map();
    current.items.forEach(item => {
      const props = { ...item.properties }; delete props._um_cart_transaction;
      const key = identity({ ...item, properties: props }); received.set(key, (received.get(key) || 0) + item.quantity);
    });
    if (sorted(Object.fromEntries(expected)) !== sorted(Object.fromEntries(received))) throw new Error(labels.changed);
    ops.save(null);
    return current;
  } catch (error) {
    try { await recoverCart(ops, transaction); } catch { throw new Error(labels.recovery); }
    error.cartRestored = true;
    throw error;
  }
}

async function exclusive(callback) {
  if (paymentBusy) return;
  paymentBusy = true; instances.forEach(instance => instance.renderControls());
  try { return await (navigator.locks ? navigator.locks.request('urban-motion-cart-payment', callback) : callback()); }
  finally { paymentBusy = false; instances.forEach(instance => instance.renderControls()); }
}

class UMCartInstallments extends HTMLElement {
  connectedCallback() {
    this.abort?.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.button = this.querySelector('[data-um-cart-pay]');
    this.status = this.querySelector('[data-um-cart-status]');
    this.dialog = this.querySelector('[data-um-cart-dialog]') || this.dialog;
    if (!this.button || !this.dialog) return;
    this.term = 4;
    this.consent = this.dialog.querySelector('[data-um-cart-consent]');
    this.continueButton = this.dialog.querySelector('[data-um-cart-continue]');
    this.closeButton = this.dialog.querySelector('[data-um-cart-close]');
    this.termInputs = [...this.dialog.querySelectorAll('[data-um-cart-term]')];
    const groupName = 'um-cart-term-' + (++sequence);
    this.termInputs.forEach(input => { input.name = groupName; });
    document.body.append(this.dialog);
    this.button.addEventListener('click', () => this.open(), { signal });
    this.closeButton.addEventListener('click', () => { if (!this.working) this.dialog.close(); }, { signal });
    this.dialog.addEventListener('cancel', event => { if (this.working) event.preventDefault(); }, { signal });
    this.dialog.addEventListener('close', () => this.finish(), { signal });
    this.dialog.addEventListener('click', event => {
      if (this.working || event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
    }, { signal });
    this.termInputs.forEach(input => input.addEventListener('change', () => {
      if (!input.checked) return;
      this.term = Number(input.value); this.consent.checked = false; this.setError(''); this.renderDialog();
    }, { signal }));
    this.consent.addEventListener('change', () => this.renderControls(), { signal });
    this.continueButton.addEventListener('click', () => this.pay(), { signal });
    instances.add(this);
    try { this.applyState(JSON.parse(this.querySelector('[data-um-cart-state]').textContent)); }
    catch { this.showStatus(labels.unavailable, true); }
    if (recoveryError) this.showStatus(recoveryError, true);
    this.refresh().catch(() => this.showStatus(labels.unavailable, true));
  }
  disconnectedCallback() {
    instances.delete(this); this.abort?.abort(); this.dialog?.close(); this.finish(); this.dialog?.remove();
  }
  showStatus(message, error = false) {
    if (!this.status) return;
    this.status.textContent = message; this.status.hidden = !message; this.status.toggleAttribute('data-error', error);
  }
  setError(message) {
    const element = this.dialog?.querySelector('[data-um-cart-error]');
    if (element) { element.textContent = message; element.hidden = !message; }
  }
  async refresh() {
    const generation = this.generation = (this.generation || 0) + 1;
    const state = await fetchState();
    if (this.isConnected && generation === this.generation) this.applyState(state);
  }
  applyState(state) {
    this.plan = hydrate(state);
    this.hidden = !state.count;
    this.showStatus(this.notice || (this.plan.terms.length ? '' : this.plan.reason), Boolean(this.notice));
    const checkout = this.closest('.cart__ctas')?.querySelector('[data-um-cart-full-checkout]');
    const text = checkout?.querySelector('.button-text');
    if (text) {
      this.defaultCheckoutText ||= text.textContent.trim();
      text.textContent = this.plan.containsRates ? fill(labels.fullCheckout, { amount: money(this.plan.total) }) : this.defaultCheckoutText;
    }
    if (this.dialog.open && this.acceptedSignature !== this.plan.signature) {
      this.acceptedSignature = this.plan.signature; this.consent.checked = false; this.setError(labels.changed);
    }
    this.renderControls(); if (this.dialog.open) this.renderDialog();
  }
  renderControls() {
    if (!this.button) return;
    const busy = paymentBusy || this.working || this.cartPending || recovering || Boolean(pendingRead());
    this.button.disabled = Boolean(busy || !this.plan?.terms.length);
    if (busy) this.button.setAttribute('aria-busy', 'true');
    else this.button.removeAttribute('aria-busy');
    if (!this.dialog) return;
    this.continueButton.disabled = Boolean(busy || !this.dialog.open || !this.consent.checked || !this.plan?.terms.includes(this.term));
    this.continueButton.textContent = busy ? labels.preparing : labels.continue;
    this.closeButton.disabled = Boolean(this.working);
    this.consent.disabled = Boolean(this.working);
    this.termInputs.forEach(input => {
      input.checked = Number(input.value) === this.term;
      input.disabled = Boolean(busy || !this.plan?.terms.includes(Number(input.value)));
    });
  }
  setText(selector, value) { const node = this.dialog.querySelector(selector); if (node) node.textContent = value; }
  renderDialog() {
    this.renderControls();
    if (!this.plan?.terms.includes(this.term)) { this.setError(this.plan?.reason || labels.unavailable); return; }
    const summary = summarize(this.plan, this.term);
    this.setText('[data-um-cart-total]', money(summary.total));
    this.setText('[data-um-cart-first]', money(summary.first));
    this.setText('[data-um-cart-remaining]', money(summary.remaining));
    this.setText('[data-um-cart-remaining-label]', planText(labels.remaining, this.term));
    this.setText('[data-um-cart-consent-text]', planText(labels.consent, this.term));
    this.setText('[data-um-cart-notice]', planText(labels.notice, this.term));
    const schedule = summary.first === summary.last ? labels.equalSchedule : labels.adjustedSchedule;
    this.setText('[data-um-cart-schedule]', fill(planText(schedule, this.term), { regular: money(summary.first), last: money(summary.last) }));
    const items = this.dialog.querySelector('[data-um-cart-plan-items]');
    items.replaceChildren(...this.plan.lines.map(line => {
      const row = document.createElement('li'), title = document.createElement('strong'), detail = document.createElement('span');
      title.textContent = line.item.title;
      const options = line.original.options.filter((_, index) => index !== line.item.paymentIndex).filter(value => value !== 'Default Title').join(' / ');
      detail.textContent = [options, fill(labels.quantity, { quantity: line.item.quantity })].filter(Boolean).join(' · ');
      row.append(title, detail); return row;
    }));
  }
  async open() {
    if (paymentBusy || this.working || recovering || [...instances].some(instance => instance.dialog?.open)) return;
    this.notice = ''; this.working = true; this.renderControls();
    try {
      if (pendingRead()) { await recoverCart(operations, pendingRead()); location.reload(); return; }
      await this.refresh();
      if (!this.plan.terms.length) throw new Error(this.plan.reason);
      this.term = this.plan.terms.includes(4) ? 4 : this.plan.terms[0];
      this.consent.checked = false; this.acceptedSignature = this.plan.signature; this.setError('');
      this.previousFocus = document.activeElement;
      this.closest('theme-drawer')?.close();
      this.previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      this.dialog.showModal(); this.renderDialog();
    } catch (error) { this.showStatus(error.message || labels.unavailable, true); }
    finally { this.working = false; this.renderControls(); if (this.dialog.open) this.closeButton.focus({ preventScroll: true }); }
  }
  finish() {
    if (this.previousOverflow !== undefined) { document.documentElement.style.overflow = this.previousOverflow; this.previousOverflow = undefined; }
    if (this.consent) this.consent.checked = false;
    if (this.previousFocus?.isConnected) this.previousFocus.focus({ preventScroll: true });
    this.previousFocus = null; this.renderControls();
  }
  async pay() {
    if (this.working || paymentBusy || !this.consent.checked || !this.dialog.open) return;
    const signature = this.acceptedSignature, term = this.term;
    await exclusive(async () => {
      this.working = true; this.renderControls(); this.setError('');
      try {
        const live = hydrate(await fetchState());
        this.applyState(live.state);
        if (live.signature !== signature || !live.terms.includes(term) || !this.consent.checked || !this.dialog.open) throw new Error(labels.changed);
        const summary = summarize(live, term);
        await convertCart(operations, paymentItems(live, term), summary.first, fingerprint(live.state));
        location.assign(cartUrl('checkout').href);
      } catch (error) {
        this.consent.checked = false; this.setError(error.message || labels.unavailable);
        if (error.cartRestored) { sessionStorage.setItem(pendingKey + '-notice', labels.restored); location.reload(); }
      } finally { this.working = false; this.renderControls(); }
    });
  }
}

async function syncCart(event) {
  const generation = ++updateGeneration;
  instances.forEach(instance => { instance.cartPending = true; instance.renderControls(); });
  try { await event.promise; } catch {}
  if (generation !== updateGeneration) return;
  try {
    const state = await fetchState();
    if (generation !== updateGeneration) return;
    instances.forEach(instance => { instance.cartPending = false; instance.applyState(state); });
  } catch { instances.forEach(instance => { instance.cartPending = false; instance.showStatus(labels.unavailable, true); instance.renderControls(); }); }
}
document.addEventListener(StandardEvents.cartLinesUpdate, syncCart);
document.addEventListener(StandardEvents.cartDiscountUpdate, syncCart);

async function cashCheckout(controller) {
  await exclusive(async () => {
    try {
      if (pendingRead()) { await recoverCart(operations, pendingRead()); location.reload(); return; }
      const live = hydrate(await fetchState());
      if (controller?.plan && live.total !== controller.plan.total) { controller.applyState(live.state); throw new Error(labels.changed); }
      if (live.containsRates) {
        if (!live.sourceValid) throw new Error(labels.unavailable);
        const items = live.lines.map(line => ({ id: line.original.id, quantity: line.item.quantity,
          properties: originalProperties(line.item), ...(line.item.sellingPlan ? { selling_plan: line.item.sellingPlan } : {}) }));
        await convertCart(operations, items, null, fingerprint(live.state));
      }
      location.assign(cartUrl('checkout').href);
    } catch (error) {
      instances.forEach(instance => instance.showStatus(error.message || labels.unavailable, true));
      if (error.cartRestored) location.reload();
    }
  });
}
function interceptCash(event, button) {
  if (!button) return;
  const controller = button.closest('.cart__ctas')?.querySelector('um-cart-installments');
  if (!pendingRead() && !controller?.plan?.containsRates) return;
  event.preventDefault(); event.stopImmediatePropagation();
  if (!paymentBusy && !recovering) cashCheckout(controller);
}
document.addEventListener('click', event => interceptCash(event, event.target.closest?.('[data-um-cart-full-checkout]')), true);
document.addEventListener('submit', event => interceptCash(event, event.submitter?.matches('[data-um-cart-full-checkout]') ? event.submitter : null), true);
addEventListener('pageshow', event => {
  if (event.persisted) {
    paymentBusy = false;
    instances.forEach(instance => { instance.working = false; instance.dialog.close(); instance.refresh().catch(() => {}); });
  }
});

const pending = pendingRead();
if (pending) {
  recovering = true;
  recoverCart(operations, pending).then(() => location.reload()).catch(() => {
    recovering = false; recoveryError = labels.recovery;
    instances.forEach(instance => { instance.showStatus(recoveryError, true); instance.renderControls(); });
  });
}
if (!customElements.get('um-cart-installments')) customElements.define('um-cart-installments', UMCartInstallments);
try {
  const notice = sessionStorage.getItem(pendingKey + '-notice');
  if (notice) { sessionStorage.removeItem(pendingKey + '-notice'); instances.forEach(instance => { instance.notice = notice; instance.showStatus(notice, true); }); }
} catch {}